import { useState, useRef, useEffect } from "react";
import { DayPicker } from "@daypicker/react";
import type { DateRange } from "@daypicker/react";
import "@daypicker/react/style.css";
import { DateTime } from "luxon";

import styles from "./index.module.scss";

type Props = {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
  placeholderText?: string;
};

const fmt = (d: Date) => DateTime.fromJSDate(d).toFormat("dd MMM yyyy");

const formatRange = (start: Date | null, end: Date | null) => {
  if (!start) return "";
  return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
};

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  placeholderText = "Select date range",
}: Props) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // When opening with a full range already set, first click resets to new start
  const resetOnNextRef = useRef(false);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open && startDate && endDate) {
      resetOnNextRef.current = true;
    }
    setOpen((prev) => !prev);
  };

  const handleSelect = (range: DateRange | undefined, triggerDate: Date) => {
    if (resetOnNextRef.current) {
      resetOnNextRef.current = false;
      onChange(triggerDate, null);
      return;
    }
    const start = range?.from ?? null;
    const end = range?.to ?? null;
    onChange(start, end);
    if (start && end && start.getTime() !== end.getTime()) setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, null);
  };

  const displayValue = formatRange(startDate, endDate);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div className={styles.inputWrap} onClick={handleToggle}>
        <span className="search-icon material-icons material-icons-outlined">
          calendar_today
        </span>
        <input
          readOnly
          className={styles.input}
          value={displayValue}
          placeholder={placeholderText}
        />
        {displayValue && (
          <button className={styles.clearBtn} onClick={handleClear}>
            <span className="material-icons" style={{ fontSize: 14 }}>
              close
            </span>
          </button>
        )}
      </div>
      {open && (
        <div className={styles.popover}>
          <DayPicker
            mode="range"
            selected={{
              from: startDate ?? undefined,
              to: endDate ?? undefined,
            }}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
